import { useEffect, useState } from "react";
import { api } from "@/lib/api";

function Pill({ color, label, testid }) {
  return (
    <span
      data-testid={testid}
      className="mq-label px-2 py-1 rounded-sm"
      style={{
        background: `${color}22`,
        color,
        border: `1px solid ${color}55`,
        letterSpacing: "0.1em",
      }}
    >
      {label}
    </span>
  );
}

function timeAgo(ts) {
  if (!ts) return "—";
  const s = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function trendColor(trend) {
  if (trend === "ACCUMULATION") return "var(--mq-green)";
  if (trend === "DISTRIBUTION") return "var(--mq-red)";
  return "var(--mq-text-2)";
}

export default function OnChainPanel({ state }) {
  const onchain = state?.onchain || {};
  const chain = onchain.onchain || {};
  const cmc = onchain.cmc || {};
  const [whales, setWhales] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/onchain/whales", { params: { limit: 1 } });
        if (mounted) setWhales(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn("onchain/whales fetch failed:", err?.message || err);
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  const lastWhale = onchain.last_whale_alert || whales[0] || null;
  const whaleRecent = lastWhale && Date.now() / 1000 - (lastWhale.detected_at || lastWhale.ts || 0) < 1800;
  const trend = onchain.holder_trend || "STABLE";
  const holders = chain.holders ?? "—";
  const hdelta = chain.holders_delta_24h ?? 0;
  const txns24h = chain.txns_24h ?? 0;
  const onchainVolUsd = chain.onchain_volume_usd_24h ?? 0;
  const cmcPrice = cmc.price_usd ?? 0;
  const cmcChange = cmc.percent_change_24h ?? 0;
  const divergence = onchain.cmc_divergence_pct ?? 0;
  const adjustedMid = onchain.adjusted_mid_price ?? 0;
  const ratio = onchain.volume_ratio ?? 0;

  return (
    <div className="mq-panel" data-testid="onchain-panel">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">
          ON-CHAIN INTELLIGENCE
        </span>
        <div className="flex items-center gap-2">
          {onchain.whale_mode && (
            <Pill color="var(--mq-red)" label="WHALE MODE" testid="onchain-whale-mode-badge" />
          )}
          <Pill color={trendColor(trend)} label={trend} testid="onchain-holder-trend-badge" />
        </div>
      </div>

      {whaleRecent && lastWhale && (
        <div
          data-testid="onchain-whale-banner"
          className="px-4 py-3 border-b text-xs font-mono"
          style={{
            background: "rgba(255,80,80,0.08)",
            color: "var(--mq-red)",
            borderColor: "var(--mq-border)",
          }}
        >
          ⚠ WHALE ALERT — {Number(lastWhale.amount || 0).toLocaleString()} →{" "}
          {lastWhale.to_exchange || (lastWhale.to || "").slice(0, 8) + "…"}{" "}
          ({timeAgo(lastWhale.detected_at || lastWhale.ts)})
        </div>
      )}

      <div className="px-4 py-3 grid grid-cols-2 gap-3 text-xs">
        <Row
          testid="onchain-holders"
          label="Holders"
          value={holders}
          extra={
            hdelta !== 0
              ? `${hdelta > 0 ? "+" : ""}${hdelta} 24h`
              : "± 0 24h"
          }
          color={hdelta > 0 ? "var(--mq-green)" : hdelta < 0 ? "var(--mq-red)" : "var(--mq-text-2)"}
        />
        <Row
          testid="onchain-txns"
          label="Txns 24h"
          value={txns24h.toLocaleString()}
        />
        <Row
          testid="onchain-volume-usd"
          label="On-chain Vol 24h"
          value={`$${Math.round(onchainVolUsd).toLocaleString()}`}
        />
        <Row
          testid="onchain-volume-ratio"
          label="On-chain / Exchange"
          value={`${ratio.toFixed(2)}×`}
          color={ratio >= 3 ? "var(--mq-yellow)" : "var(--mq-text-1)"}
        />
        <Row
          testid="onchain-cmc-price"
          label="CMC Price"
          value={`$${cmcPrice.toFixed(8)}`}
          extra={
            cmcChange !== 0
              ? `${cmcChange > 0 ? "+" : ""}${cmcChange.toFixed(2)}% 24h`
              : ""
          }
          color={cmcChange >= 0 ? "var(--mq-green)" : "var(--mq-red)"}
        />
        <Row
          testid="onchain-cmc-divergence"
          label="CMC ↔ Exch."
          value={`${divergence.toFixed(3)}%`}
          color={divergence > 0.5 ? "var(--mq-yellow)" : "var(--mq-text-1)"}
        />
        <Row
          testid="onchain-adjusted-mid"
          label="Adjusted Mid"
          value={`$${adjustedMid.toFixed(8)}`}
        />
        <Row
          testid="onchain-last-whale"
          label="Last Whale"
          value={lastWhale ? timeAgo(lastWhale.detected_at || lastWhale.ts) : "—"}
        />
      </div>
    </div>
  );
}

function Row({ label, value, extra, color, testid }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testid}>
      <span className="mq-label text-[10px]">{label}</span>
      <span className="font-mono font-semibold" style={{ color: color || "var(--mq-text-1)" }}>
        {value}
      </span>
      {extra && (
        <span className="text-[10px]" style={{ color: color || "var(--mq-text-3)" }}>
          {extra}
        </span>
      )}
    </div>
  );
}
